import { useState, useMemo } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import NoIndex from "../components/NoIndex";
import { BrandLogo } from "../components/BrandLogo";

export default function ResetPassword() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = useMemo(() => params.get("token") || "", [params]);

  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState(null);

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr(null);
    if (!token) {
      setErr("Reset link is missing its token. Please request a new reset email.");
      return;
    }
    if (pw.length < 8) {
      setErr("Password must be at least 8 characters.");
      return;
    }
    if (pw !== pw2) {
      setErr("Passwords don't match.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: pw });
      setDone(true);
      setTimeout(() => navigate("/login"), 1800);
    } catch (ex) {
      setErr(formatApiError(ex));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-md mx-auto px-4 sm:px-6 py-16">
      <NoIndex title="Reset your password · ConfirmPH" />
      <Link to="/" className="flex justify-center mb-6" data-testid="reset-brand-link">
        <BrandLogo variant="full" className="h-16 w-auto" alt="ConfirmPH" testId="reset-brand-logo" />
      </Link>
      <div className="bg-white rounded-2xl border border-slate-200 p-6 sm:p-8 shadow-sm">
        <h1 className="font-display text-2xl font-semibold text-slate-900">Set a new password</h1>
        <p className="text-sm text-slate-600 mt-2">
          Choose a strong password (at least 8 characters). After this you'll sign in normally.
        </p>

        {done ? (
          <div
            data-testid="reset-success"
            className="mt-6 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800"
          >
            Password updated. Redirecting you to sign in…
          </div>
        ) : (
          <form onSubmit={onSubmit} className="mt-6 space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-700" htmlFor="pw">New password</label>
              <input
                id="pw"
                data-testid="reset-pw-input"
                type="password"
                required
                autoComplete="new-password"
                value={pw}
                onChange={(e) => setPw(e.target.value)}
                className="mt-1.5 w-full px-3.5 py-2.5 rounded-lg border border-slate-300 focus:border-[#2D6A4F] focus:ring-1 focus:ring-[#2D6A4F] outline-none"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700" htmlFor="pw2">Confirm password</label>
              <input
                id="pw2"
                data-testid="reset-pw2-input"
                type="password"
                required
                autoComplete="new-password"
                value={pw2}
                onChange={(e) => setPw2(e.target.value)}
                className="mt-1.5 w-full px-3.5 py-2.5 rounded-lg border border-slate-300 focus:border-[#2D6A4F] focus:ring-1 focus:ring-[#2D6A4F] outline-none"
              />
            </div>
            {err && (
              <div data-testid="reset-error" className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                {err}
              </div>
            )}
            <button
              type="submit"
              data-testid="reset-submit-btn"
              disabled={loading || !token}
              className="w-full px-4 py-3 rounded-lg btn-brand font-medium disabled:opacity-60"
            >
              {loading ? "Updating…" : "Set new password"}
            </button>
          </form>
        )}

        <div className="mt-5 text-sm text-slate-600">
          <Link to="/login" className="font-medium text-[#1B4332] hover:underline" data-testid="reset-back-to-login">
            Back to sign in
          </Link>
        </div>
      </div>
    </div>
  );
}
